import {Component, OnDestroy, OnInit} from '@angular/core';
import {RxComponent} from '../../../../../core/components/common/rx.component';
import {PlatformPriceListService} from '../platform-price-list.service';
import {ModalSize, SmartModalService} from '../../../../../core/services/smart-modal.service';
import {Observable, Subject, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {NotificationService} from '../../../../../core/services/notification.service';
import {CreateNewPricingListComponent} from '../../../../../shared/components/pricing/create-new-pricing-list/create-new-pricing-list.component';
import {CreateNewPriceComponent} from '../../../../../shared/components/pricing/create-new-price/create-new-price.component';

@Component({
  selector: 'itpx-pricing-list-details',
  templateUrl: './pricing-list-details.component.html',
  styleUrls: ['./pricing-list-details.component.css']
})
export class PricingListDetailsComponent extends RxComponent implements OnInit, OnDestroy {

    private currencies = {
        EUR : 'Euro',
        USD : 'USD',
        INR : 'Indian Rupee',
        GBP : 'Pound Sterling'
    };

    private initialized = false;
    private selectedCurrency: string = this.currencies.USD;
    private pricesTableFields = null;

    private allDictionaries: any = {};
    private allCurrencies: any = {};
    private priceListItemDetails: any = {};

    private priceListItemId = null;
    private gridRefresh = new Subject();

    private sub: Subscription;
    private subAllData: Subscription;
    private subPriceList: Subscription;
    private saveProductSub: Subscription;
    private editProductSub: Subscription;
    private deleteProductSub: Subscription;
    private priceListItemDetailsSub: Subscription;

    constructor(
        private priceListService: PlatformPriceListService,
        private smartModalService: SmartModalService,
        private route: ActivatedRoute,
        private router: Router,
        private notificationService: NotificationService
    ) {
        super();
    }

    ngOnInit() {
        this.sub = this.route.params.subscribe(params => {
            this.priceListItemId = params.id;
            this.fetchAllData();
        });
        this.fetchPriceListItemDetails().subscribe();
    }

    fetchAllData() {
        this.subAllData = Observable.forkJoin([
            this.priceListService.getSupportedCurrencies(),
            this.fetchPriceListItemDetails(),
            this.priceListService.fetchAllConfigurations()
        ]).subscribe(([currencies,priceListDetails, allConfigurations]) => {
            this.allCurrencies = currencies;
            this.priceListItemDetails = priceListDetails;
            this.allDictionaries = allConfigurations;
            this.initiateTable();
            this.initialized = true;
        });
    }

    fetchPriceListItemDetails() {
        return this.priceListService.getPricesListItemDetails(this.priceListItemId).map((priceListDetails) => {
            this.priceListItemDetails = priceListDetails;
            return this.priceListItemDetails;
        });
    }

    initiateTable() {
        this.pricesTableFields = [
            {
                title: 'Name',
                config: {
                    type: 'template',
                    template: 'priceNameTemplate'
                }
            },
            {
                title: 'SKU',
                name: 'sku'
            },
            {
                title: 'List Price',
                name: (item) => {
                    return item.listPrice + ' ' + this.replaceCurrencies(item.currency.configValue);
                }
            },
            {
                title: 'Price List Price',
                name: (item) => {
                    if (item && item.pricing && item.pricing.length) {
                        return item.pricing[0].price + ' ' + this.replaceCurrencies(item.pricing[0].currency.configValue);
                    }
                    return 'N/A';
                }
            },
            {
                title: 'Action',
                config: {
                    type: 'template',
                    template: 'editActionTemplate'
                }
            }
        ];
    }

    replaceCurrencies(currency) {
        const config = {
            Euro : 'EUR'
        };
        if (config.hasOwnProperty(currency)) {
            return config[currency];
        }
        return currency;
    }

    editPropertiesOfPriceList() {
        this.fetchPriceListItemDetails().flatMap((details) => {
            return this.smartModalService.open(
                CreateNewPricingListComponent,
                {
                    dictionaries: this.allDictionaries,
                    pricingModel: this.priceListItemDetails
                },
                'Edit Price List',
                ModalSize.Custom
            )
        })
        .neverError()
        .subscribe(_ => {
            if (_.result) {
                _.result.deleteOperation ? this.deletePriceList(_.result) : this.updatePriceList(_.result);
            }
        });
    }

    updatePriceList(payload: any) {
        this.subPriceList = this.priceListService.updatePriceList(payload).subscribe((_) => {
            this.priceListItemDetails.name = _.name;
            this.notificationService.success('Price list successfully updated');
        });
    }

    deletePriceList(payload: any) {
        this.subPriceList = this.priceListService.deletePriceList(payload).subscribe((_) => {
            this.router.navigate(['/agora/pricing-data/pricing-list']);
            this.notificationService.success('Price list successfully deleted');
        });
    }

    switchTab(currency) {
        this.selectedCurrency = currency;
    }

    addPrice(edit?) {
        this.fetchPriceListItemDetails().flatMap((details) => {
            return this.smartModalService.open(
                CreateNewPriceComponent,
                {
                    editParam: edit,
                    currencies: this.allCurrencies,
                    dictionaries: this.allDictionaries,
                    serviceReference: this.priceListService,
                    priceListItemDetails: this.priceListItemDetails
                },
                !edit ? 'New Price' : 'Update Price',
                ModalSize.Custom,
            )
        })
        .neverError()
        .subscribe(_ => {
            if (_.result) {
                if (_.result.updateOperation) {
                    this.updateProductForPrice(_.result.payload);
                } else if (_.result.deleteOperation) {
                    this.deleteProductPrice(_.result.payload);
                } else if (_.result.createOperation) {
                    this.createProductForPrice(_.result.payload);
                }
            }
        });
    }

    getPriceListUrlForSelectedCurrency() {
        const currency = this.getSelectedCurrencyConfig();
        return this.priceListService.getPricesTableAPIUrl() + '?priceListId=' + this.priceListItemDetails.id + '&currencyId=' + currency.key;
    }

    getSelectedCurrencyConfig() {
        return this.allCurrencies.filter(_ => _.value === this.selectedCurrency)[0];
    }

    createProductForPrice(payload) {
        this.saveProductSub = this.priceListService.createProductForPrice(payload).subscribe((newPrice) => {
            this.notificationService.success('New price added successfully.');
            this.gridRefresh.next(1);
        });
    }

    deleteProductPrice(payload) {
        this.deleteProductSub = this.priceListService.deleteProductPrice(payload).subscribe((newPrice) => {
            this.notificationService.success('Price deleted successfully.');
            this.gridRefresh.next(1);
        });
    }

    updateProductForPrice(payload) {
        this.priceListService.editProductForPrice(payload).subscribe((newPrice) => {
            this.notificationService.success('New price updated successfully.');
            this.gridRefresh.next(1);
        });
    }

    getTabClass(currency) {
        return this.selectedCurrency === currency ? 'btn-primary' : 'btn-default';
    }

    ngOnDestroy() {
        if (this.sub) this.sub.unsubscribe();
        if (this.subAllData) this.subAllData.unsubscribe();
        if (this.subPriceList) this.subPriceList.unsubscribe();
        if (this.saveProductSub) this.saveProductSub.unsubscribe();
        if (this.editProductSub) this.editProductSub.unsubscribe();
        if (this.deleteProductSub) this.deleteProductSub.unsubscribe();
        if (this.priceListItemDetailsSub) this.priceListItemDetailsSub.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./src/app/agora/components/pricing-data/pricing-list/pricing-list-details/pricing-list-details.component.ts